/** Shared helpers for automation trigger sync between wizard, builder, and DB. */

export type TriggerType =
  | "keyword"
  | "first_message"
  | "story_reply"
  | "comment"
  | "media_type"
  | "any_message"
  | "lead_stage_entered"
  | "tag_added";

export function buildTriggerConfig(
  triggerType: string,
  config: Record<string, unknown>,
): Record<string, unknown> {
  let base: Record<string, unknown>;
  switch (triggerType) {
    case "keyword":
    case "story_reply":
    case "comment":
      base = {
        keywords: Array.isArray(config.keywords) ? config.keywords : [],
        match_mode: (config.match_mode as string) || "contains",
      };
      break;
    case "media_type":
      base = {
        media_type: (config.media_type as string) || "",
      };
      break;
    case "lead_stage_entered":
      base = {
        funnel_id: config.funnel_id || "",
        funnel_name: config.funnel_name || "",
        stage: config.stage || config.stage_slug || "",
        stage_slug: config.stage_slug || config.stage || "",
        stage_id: config.stage_id || "",
        stage_name: config.stage_name || "",
      };
      break;
    case "tag_added":
      base = {
        tag_id: config.tag_id || "",
        tag_name: config.tag_name || "",
      };
      break;
    default:
      base = {};
  }

  if (typeof config.integration_id === "string" && config.integration_id.length > 0) {
    base.integration_id = config.integration_id;
  }

  return base;
}

export function maxExecutionsForTrigger(triggerType: string): number {
  return triggerType === "first_message" ? 1 : 0;
}

export function resolveTriggerFromNode(
  triggerNode: { config?: Record<string, unknown> } | undefined,
  fallback?: { trigger_type?: string; trigger_config?: Record<string, unknown> },
): {
  trigger_type: string;
  trigger_config: Record<string, unknown>;
  max_executions_per_contact: number;
} {
  const triggerType =
    (triggerNode?.config?.trigger_type as string) ||
    fallback?.trigger_type ||
    "keyword";
  const nodeConfig = triggerNode?.config ?? {};
  const built = buildTriggerConfig(triggerType, nodeConfig);
  const builtKeywords = (built.keywords as string[] | undefined) ?? [];
  const triggerConfig =
    builtKeywords.length > 0
      ? built
      : fallback?.trigger_config && Object.keys(fallback.trigger_config).length > 0
        ? fallback.trigger_config
        : built;

  return {
    trigger_type: triggerType,
    trigger_config: triggerConfig,
    max_executions_per_contact: maxExecutionsForTrigger(triggerType),
  };
}

export function remapCompiledFlowIds(
  compiledFlow: { nodes: Array<{ id: string }>; edges: Array<{ source: string; target: string; branch: string }> } | null,
  idMap: Map<string, string>,
): typeof compiledFlow {
  if (!compiledFlow) return null;
  return {
    nodes: compiledFlow.nodes.map((n) => ({
      ...n,
      id: idMap.get(n.id) ?? n.id,
    })),
    edges: compiledFlow.edges.map((e) => ({
      ...e,
      source: idMap.get(e.source) ?? e.source,
      target: idMap.get(e.target) ?? e.target,
    })),
  };
}
